import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { NotaFiscal, ItemNotaFiscal, Material } from '../../types';
import { ArrowLeftIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const NotaFiscalConferencia: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [nota, setNota] = useState<NotaFiscal | null>(null);
  const [itens, setItens] = useState<ItemNotaFiscal[]>([]);
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mapeamentos, setMapeamentos] = useState<Record<number, number | null>>({});

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [notaResponse, materiaisResponse] = await Promise.all([
        estoqueApi.getNotaFiscal(Number(id)),
        estoqueApi.getMateriais({})
      ]);

      setNota(notaResponse);
      setItens(notaResponse.itens || []);
      setMateriais(materiaisResponse.results || []);

      // Inicializar mapeamentos com valores já existentes
      const mappings: Record<number, number | null> = {};
      (notaResponse.itens || []).forEach(item => {
        if (item.material) {
          const itemId = typeof item.id === 'string' ? Number(item.id) : item.id;
          mappings[itemId] = item.material;
        }
      });
      setMapeamentos(mappings);
    } catch (err: any) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMapeamentoChange = (itemId: number | string, materialId: string) => {
    const id = typeof itemId === 'string' ? Number(itemId) : itemId;
    setMapeamentos(prev => ({
      ...prev,
      [id]: materialId ? Number(materialId) : null
    }));
  };

  const getMapeamento = (itemId: number | string): number | null => {
    const id = typeof itemId === 'string' ? Number(itemId) : itemId;
    return mapeamentos[id] ?? null;
  };

  const handleConfirmar = async () => {
    if (!nota) return;

    // Verificar se todos os itens foram mapeados
    const itensSemMapeamento = itens.filter(item => !getMapeamento(item.id));

    if (itensSemMapeamento.length > 0) {
      if (!window.confirm(
        `${itensSemMapeamento.length} item(ns) não foram mapeados. Deseja continuar mesmo assim?`
      )) {
        return;
      }
    }

    try {
      setSaving(true);

      // Preparar dados dos itens para enviar ao backend
      const itensParaConferir = itens
        .filter(item => getMapeamento(item.id) !== null)
        .map(item => ({
          item_id: typeof item.id === 'string' ? Number(item.id) : item.id,
          material_id: getMapeamento(item.id)!,
          criar_novo: false
        }));

      // Marcar nota como conferida (backend vai mapear os itens)
      await estoqueApi.conferirNotaFiscal(Number(id), itensParaConferir);

      alert('Nota fiscal conferida com sucesso!');
      navigate(`/estoque/notas-fiscais/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao confirmar conferência');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  if (error || !nota) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error || 'Nota fiscal não encontrada'}
      </div>
    );
  }

  const itensMapeados = itens.filter(item => getMapeamento(item.id) !== null).length;
  const todosItensConferidos = itensMapeados === itens.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/estoque/notas-fiscais/${id}`)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-base font-bold text-gray-900">
              Conferência NF-e {nota.numero_nota}/{nota.serie}
            </h1>
            <p className="text-xs text-gray-600">{nota.emitente_razao_social}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-600">
            {itensMapeados} de {itens.length} itens mapeados
          </span>
          <button
            onClick={handleConfirmar}
            disabled={saving}
            className={`flex items-center px-4 py-2 text-white text-sm rounded-md ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : todosItensConferidos
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          >
            <CheckIcon className="h-4 w-4 mr-1.5" />
            {saving ? 'Salvando...' : 'Confirmar Conferência'}
          </button>
        </div>
      </div>

      {/* Instruções */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Instruções:</h3>
        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
          <li>Para cada item da nota, selecione o material correspondente no estoque</li>
          <li>Itens já mapeados automaticamente aparecem pré-selecionados</li>
          <li>Você pode deixar itens sem mapear se necessário</li>
          <li>Após conferir, a nota estará pronta para ser lançada no estoque</li>
        </ul>
      </div>

      {/* Tabela de Conferência */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código NFe</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição NFe</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Qtd</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor Unit.</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Material no Estoque</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {itens.map((item) => {
              const materialId = getMapeamento(item.id);
              const materialSelecionado = materiais.find(m => {
                const mId = typeof m.id === 'string' ? Number(m.id) : m.id;
                return mId === materialId;
              });
              const estaMapeado = materialId !== null;

              return (
                <tr key={item.id} className={estaMapeado ? 'bg-green-50' : 'bg-yellow-50'}>
                  <td className="px-3 py-2 text-xs text-gray-900">{item.numero_item}</td>
                  <td className="px-3 py-2 text-xs text-gray-900 font-mono">{item.codigo_produto}</td>
                  <td className="px-3 py-2 text-xs text-gray-900">{item.descricao}</td>
                  <td className="px-3 py-2 text-xs text-center text-gray-900">
                    {item.quantidade} {item.unidade}
                  </td>
                  <td className="px-3 py-2 text-xs text-right text-gray-900">
                    {formatCurrency(item.valor_unitario)}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={materialId || ''}
                      onChange={(e) => handleMapeamentoChange(item.id, e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Selecione um material --</option>
                      {materiais.map(material => (
                        <option key={material.id} value={material.id}>
                          {material.codigo} - {material.nome}
                        </option>
                      ))}
                    </select>
                    {materialSelecionado && (
                      <div className="mt-1 text-xs text-gray-500">
                        Saldo atual: {materialSelecionado.saldo_atual} {materialSelecionado.unidade}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {estaMapeado ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckIcon className="h-3 w-3 mr-1" />
                        Mapeado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <XMarkIcon className="h-3 w-3 mr-1" />
                        Pendente
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resumo */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Resumo da Conferência</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded">
            <p className="text-2xl font-bold text-blue-600">{itens.length}</p>
            <p className="text-xs text-gray-600">Total de Itens</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded">
            <p className="text-2xl font-bold text-green-600">{itensMapeados}</p>
            <p className="text-xs text-gray-600">Mapeados</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded">
            <p className="text-2xl font-bold text-yellow-600">{itens.length - itensMapeados}</p>
            <p className="text-xs text-gray-600">Pendentes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotaFiscalConferencia;
